/**
 * Firebase Service - Centralized Firebase initialization and access
 * Simplified for Landing Page (only needs Functions for contact form)
 */

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getFunctions, Functions, connectFunctionsEmulator } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { getFirebaseConfig } from '../config/firebase.config';
import { APP_CHECK_CONFIG } from '../config/appcheck.config';
import { infoLog } from '../utils/logger';

class FirebaseService {
  private static instance: FirebaseService;
  private app: FirebaseApp | null = null;
  private functionsInstance: Functions | null = null;
  private initialized = false;

  private constructor() {}

  public static getInstance(): FirebaseService {
    if (!FirebaseService.instance) {
      FirebaseService.instance = new FirebaseService();
    }
    return FirebaseService.instance;
  }

  public async initialize(): Promise<void> {
    if (this.initialized) {
      infoLog('[Firebase] Already initialized');
      return;
    }

    try {
      const config = getFirebaseConfig();
      infoLog('[Firebase] Initializing with project:', config.projectId);

      this.app = initializeApp(config);

      // Initialize App Check for bot protection
      if (typeof window !== 'undefined') {
        // Use debug token in local development (no reCAPTCHA calls, no cost)
        if (import.meta.env.DEV) {
          (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN: boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
        }
        
        try {
          initializeAppCheck(this.app, {
            provider: new ReCaptchaEnterpriseProvider(APP_CHECK_CONFIG.landing.siteKey),
            isTokenAutoRefreshEnabled: APP_CHECK_CONFIG.landing.isTokenAutoRefreshEnabled
          });
          infoLog('[Firebase] App Check initialized for Landing page');
        } catch (error) {
          console.warn('[Firebase] App Check initialization failed:', error);
        }
      }

      this.functionsInstance = getFunctions(this.app, 'europe-west1');

      if (import.meta.env.VITE_ENVIRONMENT === 'local' && import.meta.env.VITE_USE_EMULATORS === 'true') {
        infoLog('[Firebase] Connecting to Functions emulator');
        connectFunctionsEmulator(this.functionsInstance, 'localhost', 5001);
      }

      this.initialized = true;
      infoLog('[Firebase] Initialization complete');
    } catch (error) {
      console.error('[Firebase] Initialization failed:', error);
      throw error;
    }
  }

  public getApp(): FirebaseApp {
    if (!this.app) throw new Error('Firebase not initialized. Call initialize() first.');
    return this.app;
  }

  public getFunctions(): Functions {
    if (!this.functionsInstance) throw new Error('Functions not initialized. Call initialize() first.');
    return this.functionsInstance;
  }

  public isInitialized(): boolean {
    return this.initialized;
  }
}

export const firebaseService = FirebaseService.getInstance();
export const getFirebaseApp = () => firebaseService.getApp();
export const getFirebaseFunctions = () => firebaseService.getFunctions();

